/**
 * Pack PDP + Armar — /market/:slug/bundle/:bundleSlug
 * Chrome de tienda + productos (catálogo + bundle_only) + definición publicada.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { useRef } from "react";
import { Package } from "lucide-react";
import { BundleRuntime } from "@/components/bundles/BundleRuntime";
import { BundleCoverMedia } from "@/components/bundles/BundleCoverMedia";
import {
  MarketStoreChrome,
  productsForBundleRuntime,
} from "@/components/market/MarketStoreChrome";
import { MarketBreadcrumb } from "@/components/market/MarketBreadcrumb";
import { Button } from "@/components/ui/button";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import type { FlowDefinition } from "@/lib/flow-engine/types";
import { MarketBundleSkeleton } from "@/components/market/MarketSkeletons";
import {
  dbSmGetBundleBySlug,
  dbSmGetTenantBySlug,
} from "@/lib/db-service";

type BundlePageLoader = {
  catalog: MarketCatalogResult;
  definition: FlowDefinition | null;
  error: string | null;
};

const fetchBundlePage = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string; bundleSlug: string }) => input)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data }): Promise<BundlePageLoader> => {
    const catalog = await loadMarketCatalog(data.slug, { request: getRequest() });
    if (!catalog.ok) {
      return { catalog, definition: null, error: catalog.error };
    }
    try {
      const tenant = await dbSmGetTenantBySlug(data.slug);
      if (!tenant) {
        return { catalog, definition: null, error: "Mercado no encontrado" };
      }
      const rec = await dbSmGetBundleBySlug(tenant.id, data.bundleSlug, {
        publishedOnly: true,
      });
      const def = rec?.published ?? null;
      if (!def) {
        return { catalog, definition: null, error: "Pack no encontrado" };
      }
      return { catalog, definition: def as FlowDefinition, error: null };
    } catch (e) {
      return {
        catalog,
        definition: null,
        error: e instanceof Error ? e.message : "Error al cargar el pack",
      };
    }
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/bundle/$bundleSlug")({
  pendingComponent: MarketBundleSkeleton,
  pendingMs: 0,
  loader: async ({ params }) =>
    fetchBundlePage({ data: { slug: params.slug, bundleSlug: params.bundleSlug } }),
  component: MarketBundlePage,
  head: ({ params, loaderData }) => {
    const data = loaderData as BundlePageLoader | undefined;
    const name =
      data?.catalog?.ok ? data.catalog.data.tenant.name : params.slug;
    const packName = data?.definition?.name ?? params.bundleSlug;
    return {
      meta: [{ title: `${packName} — ${name}` }],
      links: [{ rel: "manifest", href: `/api/manifest/${params.slug}` }, FONT_LINK],
    };
  },
});

function MarketBundlePage() {
  const { slug, bundleSlug } = Route.useParams();
  const navigate = useNavigate();
  const builderRef = useRef<HTMLDivElement | null>(null);
  const { catalog, definition: flow, error } = Route.useLoaderData() as BundlePageLoader;

  const offline = renderMarketPublicOffline(catalog);
  if (offline) return offline;

  if (!catalog.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <p className="text-muted-foreground">Pack no disponible.</p>
      </div>
    );
  }

  const data = catalog.data;

  if (!flow || error) {
    return (
      <MarketStoreChrome slug={slug} data={data}>
        <div className="flex flex-col items-center gap-3 px-4 py-20 text-center">
          <Package className="h-12 w-12 text-muted-foreground" />
          <h1 className="text-xl font-semibold">{error ?? "Pack no encontrado"}</h1>
          <Link
            to="/market/$slug/bundles"
            params={{ slug }}
            className="text-sm font-medium underline-offset-2 hover:underline"
          >
            Ver packs
          </Link>
        </div>
      </MarketStoreChrome>
    );
  }

  const products = productsForBundleRuntime(data);
  const summary = (data.bundles ?? []).find((b) => b.slug === bundleSlug);
  const imageUrl = flow.imageUrl ?? summary?.imageUrl ?? summary?.imageMosaic?.[0] ?? null;
  const imageMosaic = summary?.imageMosaic ?? null;

  return (
    <MarketStoreChrome
      slug={slug}
      data={data}
      activeHref={`/market/${slug}/bundle/${bundleSlug}`}
    >
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-12">
        <MarketBreadcrumb
          items={[
            { label: data.tenant.name, href: `/market/${slug}` },
            { label: "Packs", href: `/market/${slug}/bundles` },
            { label: flow.name },
          ]}
          className="mb-6"
        />

        <div className="mb-10 grid gap-8 md:grid-cols-2 md:items-center">
          <BundleCoverMedia
            imageUrl={imageUrl}
            mosaic={imageMosaic}
            className="aspect-[4/3] rounded-2xl"
            imgClassName="transition-transform duration-300"
            alt={flow.name}
          />
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Pack personalizable
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight md:text-4xl">{flow.name}</h1>
            {flow.description ? (
              <p className="mt-3 leading-relaxed text-muted-foreground">{flow.description}</p>
            ) : (
              <p className="mt-3 text-muted-foreground">
                Elegí cada ítem y armá tu selección a tu gusto.
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() =>
                  builderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                Armar pack
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => void navigate({ to: "/market/$slug/bundles", params: { slug } })}
              >
                Ver otros packs
              </Button>
            </div>
          </div>
        </div>

        <div ref={builderRef} id="armar" className="scroll-mt-28 border-t pt-8">
          <h2 className="mb-4 text-lg font-semibold tracking-tight">Armá tu selección</h2>
          <BundleRuntime
            definition={flow}
            products={products}
            categories={data.categories}
            channel="storefront"
            marketSlug={slug}
            onCompleted={() => {
              void navigate({ to: "/market/$slug/checkout", params: { slug } });
            }}
          />
        </div>
      </div>
    </MarketStoreChrome>
  );
}
